import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import { GREY } from '@/constants/color';
import { Close } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, FormLabel, IconButton, TextField } from '@mui/material';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (templateName: string) => Promise<void>;
  templateNames: string[];
}

interface IFormState {
  templateName: string;
}

const SaveAsTemplateModal = ({ open, onClose, onSubmit, templateNames }: IProps) => {
  const [loading, setLoading] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      templateName: '',
    } as IFormState,
  });

  const { handleSubmit, control, reset } = formMethods;

  const onModalClose = () => {
    onClose();
    reset();
  };

  const _onSubmit = async (data: IFormState) => {
    setLoading(true);
    try {
      await onSubmit(data.templateName);
      setLoading(false);

      onModalClose();
      toast.success('Template saved successfully');
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error('Something went wrong');
    }
  };

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Dialog open={open} onClose={onModalClose}>
        <DialogContent
          sx={{
            display: 'flex',
            padding: '16px',
            width: '380px',
            gap: '16px',
          }}
        >
          <FormProvider {...formMethods}>
            <form className='w-full' onSubmit={handleSubmit(_onSubmit)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <FormLabel sx={{ fontWeight: 600, fontSize: '24px', marginBottom: '4px' }} htmlFor='template-name'>
                  Save Template
                </FormLabel>
                <IconButton aria-label='close' onClick={onModalClose} sx={{ padding: 0 }}>
                  <Close />
                </IconButton>
              </Box>
              <p className={`text-[14px] text-[${GREY}] mb-2`}>Give your template a name to save it</p>
              <Controller
                name='templateName'
                control={control}
                rules={{
                  required: {
                    message: 'Template Name is required',
                    value: true,
                  },
                  validate: value => {
                    if (templateNames.includes(value)) return 'Template Name must be unique.';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id='template-name'
                    error={!!error}
                    helperText={error?.message}
                    placeholder='Enter Template Name'
                    sx={{
                      marginBottom: '12px',
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: 250,
                      },
                    }}
                  />
                )}
              />
              <div className='flex gap-[9px]'>
                <Button variant='outlined' sx={{ flex: 1 }} onClick={onModalClose} type='reset'>
                  Cancel
                </Button>
                <Button variant='contained' sx={{ flex: 1 }} type='submit'>
                  Save
                </Button>
              </div>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
    </LoadingBackdropWrapper>
  );
};

export default SaveAsTemplateModal;
